import { CRUDService } from '@/services/CRUD'
import { ICRUDService, QueryParams } from '@/services/types'
import { EventLp } from './types'
import { AxiosResponse } from 'axios'
import {
  EventType,
  ParticipantType,
} from '@/modules/expert/events/schemas/schemas'
import type { EveLinkDto } from './types/eve-link'

export type EveLinkRequestPayload =
  | { action: 'REPORT' | 'DELETE' }
  | { action: 'EDIT'; message: string }

export type GuestNotificationDto = {
  id: string
  title?: string
  message?: string
  description?: string
  body?: string
  read: boolean
  createdDate?: string
  createdAt?: string
  updatedDate?: string
  type?: string
  actionId?: string | null
  link?: string | null
  actionUrl?: string | null
}

export class GuestService extends CRUDService implements ICRUDService<EventLp> {
  static async getEventLp(eventId: string) {
    return await this.handlePromise<EventLp>(this.api.get(`/event/${eventId}`))
  }

  static async getEventLpDeslogado(eventId: string) {
    return await this.handlePromise<EventLp>(
      this.api.get(`/event/${eventId}/public`)
    )
  }

  static async list(params?: QueryParams): Promise<AxiosResponse<EventType[]>> {
    const alwaysSendKeys = ['page', 'size']

    const cleanedParams = Object.fromEntries(
      Object.entries(params || {}).filter(([key, value]) => {
        if (alwaysSendKeys.includes(key)) return true
        return value !== undefined && value !== null && value !== ''
      })
    )

    const response = await this.api.get(`/event`, {
      params: cleanedParams,
    })

    if (response?.data) {
      response.data = mapEventData(response.data)
    }

    return response
  }

  static async getParticipantsList(eventId: string) {
    return await this.handlePromise<ParticipantType[]>(
      this.api.get(`/event/${eventId}/registration`)
    )
  }

  static async listEveLinks(
    params?: QueryParams
  ): Promise<AxiosResponse<EveLinkDto[]>> {
    const cleanedParams = Object.fromEntries(
      Object.entries(params || {}).filter(([, value]) => {
        return value !== undefined && value !== null && value !== ''
      })
    )

    return await this.api.get(`/eve-link`, { params: cleanedParams })
  }

  static async requestEveLink(
    eveLinkId: string,
    payload: EveLinkRequestPayload
  ): Promise<AxiosResponse<void>> {
    return await this.api.post(`/eve-link/${eveLinkId}/request`, payload)
  }

  static async listNotifications(
    params?: QueryParams
  ): Promise<AxiosResponse<GuestNotificationDto[]>> {
    const cleanedParams = Object.fromEntries(
      Object.entries(params || {}).filter(([, value]) => {
        return value !== undefined && value !== null && value !== ''
      })
    )

    return await this.api.get(`/account/notification`, {
      params: {
        sort: 'createdDate,desc',
        ...cleanedParams,
      },
    })
  }

  static async setNotificationRead(
    notificationId: string,
    read: boolean
  ): Promise<AxiosResponse<void>> {
    return await this.api.put(`/account/notification/${notificationId}`, null, {
      params: { read },
    })
  }

  static async setAllNotificationsRead(
    read: boolean
  ): Promise<AxiosResponse<void>> {
    return await this.api.put(`/account/notification`, null, {
      params: { read },
    })
  }

  static async readNotification(
    notificationId: string
  ): Promise<AxiosResponse<void>> {
    return await this.setNotificationRead(notificationId, true)
  }

  static async unreadNotification(
    notificationId: string
  ): Promise<AxiosResponse<void>> {
    return await this.setNotificationRead(notificationId, false)
  }

  static async readAllNotifications(): Promise<AxiosResponse<void>> {
    return await this.setAllNotificationsRead(true)
  }

  static async unreadAllNotifications(): Promise<AxiosResponse<void>> {
    return await this.setAllNotificationsRead(false)
  }
}

function mapEventData(events: EventType[]): EventType[] {
  return events.map((event) => ({
    ...event,
    city: event.location?.cityName || '-',
    state: event.location?.stateName || '-',
    country: event.location?.countryName || '-',
    format: event.location?.type || '-',
    phase: event.status || '-',
  }))
}
